// Widget pecahan: bentuk yang bisa diarsir (pizza/batang), pencari pecahan senilai,
// dan animasi "kali/bagi pembilang dan penyebut dengan bilangan yang sama".
import { pecahan, simp, pengatur, pasangPengatur, letup, animasi, gerakHalus } from '../alat.js';

const R = 90, CX = 100, CY = 100;
const polar = (a, r = R) => [CX + r * Math.sin(a), CY - r * Math.cos(a)];

// Potongan pizza ke-i dari d (SVG path).
function irisan(i, d) {
  if (d === 1) return `M${CX} ${CY - R}A${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R}Z`;
  const a0 = (i / d) * 2 * Math.PI, a1 = ((i + 1) / d) * 2 * Math.PI;
  const [x0, y0] = polar(a0), [x1, y1] = polar(a1);
  return `M${CX} ${CY}L${x0.toFixed(2)} ${y0.toFixed(2)}A${R} ${R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}Z`;
}

/** SVG batang pecahan: d bagian, `arsir` = Set indeks yang diarsir. */
export function svgBatang(d, arsir, { w = 300, h = 56, kelas = '', bisaKetuk = false, label } = {}) {
  const lebar = w / d;
  const bagian = Array.from({ length: d }, (_, i) => `<rect class="wg-bagian${arsir.has(i) ? ' on' : ''}" data-i="${i}" x="${(i * lebar + 1).toFixed(2)}" y="1" width="${(lebar - 2).toFixed(2)}" height="${h - 2}" rx="6"${bisaKetuk ? ` tabindex="0" role="button" aria-label="Bagian ${i + 1}${arsir.has(i) ? ', diarsir' : ''}"` : ''}/>`).join('');
  return `<svg class="wg-batang ${kelas}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label || `batang dibagi ${d}`}">${bagian}</svg>`;
}

function svgPizza(d, arsir, bisaKetuk) {
  const iris = Array.from({ length: d }, (_, i) => `<path class="wg-bagian pizza${arsir.has(i) ? ' on' : ''}" data-i="${i}" d="${irisan(i, d)}"${bisaKetuk ? ` tabindex="0" role="button" aria-label="Potongan ${i + 1}${arsir.has(i) ? ', diarsir' : ''}"` : ''}/>`).join('');
  return `<svg class="wg-pizza" viewBox="0 0 200 200" role="img" aria-label="pizza dibagi ${d} potong"><circle cx="${CX}" cy="${CY}" r="${R + 6}" class="wg-kulit"/>${iris}</svg>`;
}

const labelPecahan = (a, d) => `<div class="wg-pecahan-besar" aria-live="polite"><b class="a">${a}</b><small>pembilang: bagian yang diarsir</small><span class="garis"></span><span></span><b class="b">${d}</b><small>penyebut: banyak bagian sama besar</small></div>`;

// Tap/Enter/Spasi pada bagian untuk mengarsir.
function pasangKetuk(host, toggle) {
  host.addEventListener('click', e => { const b = e.target.closest('[data-i]'); if (b) toggle(+b.dataset.i); });
  host.addEventListener('keydown', e => {
    const b = e.target.closest('[data-i]');
    if (b && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(+b.dataset.i); host.querySelector(`[data-i="${b.dataset.i}"]`)?.focus(); }
  });
}

/**
 * Arsir bentuk sampai menjadi pecahan target.
 * param: { bentuk: 'lingkaran'|'batang', target: [a, b], penyebut: awal, ubahPenyebut: bool, samaNilai: bool }
 */
export function pecahanBentuk(host, p, ctx) {
  const st = { d: p.penyebut || p.target[1], arsir: new Set() };
  host.innerHTML = `<div class="wg wg-bentuk">
    ${p.ubahPenyebut ? `<div class="wg-alat">${pengatur('d', 'Dibagi menjadi', st.d, { min: 1, max: 12 })}</div>` : ''}
    <div class="wg-gambar"></div>
    <div class="wg-label"></div>
  </div>`;
  const gambar = host.querySelector('.wg-gambar'), label = host.querySelector('.wg-label');
  const gambarUlang = (animasikan) => {
    gambar.innerHTML = p.bentuk === 'lingkaran' ? svgPizza(st.d, st.arsir, true) : svgBatang(st.d, st.arsir, { bisaKetuk: true, w: 320, h: 70 });
    label.innerHTML = labelPecahan(st.arsir.size, st.d);
    if (animasikan) animasi(gambar.firstElementChild, [{ transform: 'scale(.92) rotate(-6deg)', opacity: .5 }, { transform: 'none', opacity: 1 }], { duration: 380 });
  };
  const cek = () => {
    const [ta, tb] = p.target, a = st.arsir.size, d = st.d;
    const tepat = p.samaNilai ? a * tb === ta * d && a > 0 : a === ta && d === tb;
    if (tepat) ctx.selesai(`Betul! Itu ${pecahan(a, d)}: ${a} dari ${d} bagian yang sama besar.`);
    else if (d === tb && a > ta) ctx.ucap(`Kebanyakan! Kita perlu ${ta} bagian saja.`, 'wow');
    else if (p.ubahPenyebut && d !== tb && a > 0) ctx.ucap(`Sekarang ${pecahan(a, d)}. Penyebutnya harus ${tb}, ya.`, 'think');
    else ctx.ucap(a ? `Sekarang ${pecahan(a, d)}.` : '');
  };
  pasangKetuk(gambar, i => {
    st.arsir.has(i) ? st.arsir.delete(i) : st.arsir.add(i);
    ctx.sfx.tap();
    gambarUlang(false);
    letup(gambar.querySelector(`[data-i="${i}"]`));
    cek();
  });
  if (p.ubahPenyebut) pasangPengatur(host, st, (_, v) => { st.d = v; st.arsir.clear(); gambarUlang(true); ctx.ucap(`Sekarang dibagi menjadi ${v} bagian yang sama besar.`); });
  gambarUlang(false);
}

/**
 * Temukan pecahan senilai: batang atas tetap, batang bawah diatur & diarsir.
 * param: { asal: [a, b], cari: jumlah pecahan senilai yang harus ditemukan }
 */
export function senilai(host, p, ctx) {
  const [a0, b0] = p.asal;
  const st = { d: b0 * 2, arsir: new Set(), temu: new Set() };
  host.innerHTML = `<div class="wg wg-senilai">
    <div class="wg-baris"><span class="wg-tag">${pecahan(a0, b0)}</span>${svgBatang(b0, new Set(Array.from({ length: a0 }, (_, i) => i)), { w: 320, h: 54, kelas: 'acuan' })}</div>
    <div class="wg-baris"><span class="wg-tag" data-kini>?</span><div class="wg-bawah"></div></div>
    <div class="wg-alat">${pengatur('d', 'Bagi batang bawah', st.d, { min: 2, max: 12 })}</div>
    <div class="wg-temuan" aria-live="polite"><span>Pecahan senilai yang kamu temukan:</span><span class="wg-chips"></span></div>
  </div>`;
  const bawah = host.querySelector('.wg-bawah'), kini = host.querySelector('[data-kini]'), chips = host.querySelector('.wg-chips');
  const gambar = () => {
    bawah.innerHTML = svgBatang(st.d, st.arsir, { w: 320, h: 54, bisaKetuk: true });
    kini.innerHTML = st.arsir.size ? pecahan(st.arsir.size, st.d) : '?';
    chips.innerHTML = [...st.temu].map(k => { const [x, y] = k.split('/'); return `<span class="chip ok">${pecahan(+x, +y)} ✓</span>`; }).join('') || '<span class="muted">belum ada</span>';
  };
  const cek = () => {
    const a = st.arsir.size, d = st.d;
    const sama = a * b0 === a0 * d && a > 0;
    host.querySelector('.wg-senilai').classList.toggle('cocok', sama);
    if (!sama) { ctx.ucap(a ? `${pecahan(a, d)} belum sama panjang dengan ${pecahan(a0, b0)}.` : ''); return; }
    if (d === b0) { ctx.ucap(`Itu pecahan yang sama persis. Coba bagi batang bawah dengan angka lain!`, 'think'); return; }
    const k = `${a}/${d}`;
    if (!st.temu.has(k)) { st.temu.add(k); ctx.sfx.ok(); }
    gambar();
    host.querySelector('.wg-senilai').classList.add('cocok');
    if (st.temu.size >= (p.cari || 3)) ctx.selesai(`Hebat! ${[...st.temu].map(x => pecahan(...x.split('/').map(Number))).join(', ')} semuanya senilai dengan ${pecahan(a0, b0)}.`);
    else ctx.ucap(`Yes! ${pecahan(a, d)} = ${pecahan(a0, b0)}. Cari ${(p.cari || 3) - st.temu.size} lagi!`, 'cheer');
  };
  pasangKetuk(bawah, i => { st.arsir.has(i) ? st.arsir.delete(i) : st.arsir.add(i); ctx.sfx.tap(); gambar(); cek(); });
  pasangPengatur(host, st, (_, v) => { st.d = v; st.arsir.clear(); gambar(); cek(); animasi(bawah, [{ opacity: .3 }, { opacity: 1 }], { duration: 250 }); });
  gambar();
}

/**
 * Kali (atau bagi) pembilang dan penyebut dengan bilangan yang sama, dengan animasi
 * potongan batang yang terbelah / tergabung.
 * param: { asal: [a, b], mode: 'kali'|'bagi', pengali: [2,3,4], coba: berapa kali harus mencoba }
 */
export function kaliSenilai(host, p, ctx) {
  const [a0, b0] = p.asal;
  const bagi = p.mode === 'bagi';
  const pilihan = p.pengali || (bagi ? [2, 3, 4, 6].filter(k => a0 % k === 0 && b0 % k === 0) : [2, 3, 4]);
  const dicoba = new Set();
  host.innerHTML = `<div class="wg wg-kali">
    <div class="wg-tombol">${pilihan.map(k => `<button type="button" class="btn btn-sm" data-k="${k}">${bagi ? ':' : '×'} ${k}</button>`).join('')}</div>
    <div class="wg-persamaan" aria-live="polite"></div>
    <div class="wg-baris"><span class="wg-tag">${pecahan(a0, b0)}</span>${svgBatang(b0, new Set(Array.from({ length: a0 }, (_, i) => i)), { w: 320, h: 54, kelas: 'acuan' })}</div>
    <div class="wg-baris"><span class="wg-tag" data-hasil>?</span><div class="wg-hasil"></div></div>
  </div>`;
  const pers = host.querySelector('.wg-persamaan'), hasil = host.querySelector('.wg-hasil'), tag = host.querySelector('[data-hasil]');
  pers.innerHTML = `<span class="muted">Pilih tombol ${bagi ? 'pembagi' : 'pengali'} di atas.</span>`;
  const jalankan = async k => {
    const a = bagi ? a0 / k : a0 * k, b = bagi ? b0 / k : b0 * k;
    host.querySelectorAll('[data-k]').forEach(x => x.classList.toggle('btn-sun', +x.dataset.k === k));
    pers.innerHTML = `<span class="wg-rumus"><span class="fr-baris">${pecahan(a0, b0)}</span><span class="op">=</span><span class="wg-susun"><span><b>${a0}</b> <i>${bagi ? ':' : '×'} ${k}</i></span><span class="g"></span><span><b>${b0}</b> <i>${bagi ? ':' : '×'} ${k}</i></span></span><span class="op">=</span><span class="fr-baris hasil">${pecahan(a, b)}</span></span>`;
    // Animasi: batang hasil mula-mula sama dengan asal, lalu tiap bagian terbelah (kali) atau bergabung (bagi).
    hasil.innerHTML = svgBatang(b0, new Set(Array.from({ length: a0 }, (_, i) => i)), { w: 320, h: 54 });
    tag.innerHTML = pecahan(a0, b0);
    if (gerakHalus()) await new Promise(r => setTimeout(r, 350));
    hasil.innerHTML = svgBatang(b, new Set(Array.from({ length: a }, (_, i) => i)), { w: 320, h: 54, kelas: 'baru' });
    tag.innerHTML = pecahan(a, b);
    letup(pers.querySelector('.hasil'));
    dicoba.add(k);
    ctx.sfx.tap();
    const perlu = Math.min(p.coba || 2, pilihan.length);
    if (dicoba.size >= perlu) ctx.selesai(bagi
      ? `Lihat! Batangnya sama panjang. ${pecahan(a0, b0)} bisa disederhanakan menjadi ${pecahan(...simp(a0, b0))} (dibagi FPB-nya, ${a0 / simp(a0, b0)[0]}).`
      : `Batangnya tetap sama panjang, hanya potongannya makin kecil. Jadi ${pecahan(a0, b0)} = ${pecahan(a, b)}.`);
    else ctx.ucap(`Batangnya tetap sama panjang! Coba tombol yang lain juga.`, 'wow');
  };
  host.querySelectorAll('[data-k]').forEach(b => b.addEventListener('click', () => jalankan(+b.dataset.k)));
}

export const WIDGET_PECAHAN = { pecahanBentuk, senilai, kaliSenilai };
